import React, { useState } from "react";
import { UserProfile } from "../auth/session";
import { StatCard } from "../components/StatCard";

interface AcademyDashboardProps {
  user: UserProfile;
}

interface BatchInfo {
  id: string;
  name: string;
  level: "Beginner" | "Intermediate" | "Advanced";
  studentCount: number;
  coach: string;
  schedule: string;
  averageRating: number;
}

const BATCHES: BatchInfo[] = [
  {
    id: "batch-01",
    name: "Intermediate Batch A",
    level: "Intermediate",
    studentCount: 14,
    coach: "Coach Ramesh S. (FIDE Trainer)",
    schedule: "Sat & Sun 10:00 AM - 12:00 PM",
    averageRating: 1545,
  },
  {
    id: "batch-02",
    name: "Junior Beginners Batch",
    level: "Beginner",
    studentCount: 12,
    coach: "Coach Anandhi V.",
    schedule: "Tue & Thu 4:30 PM - 6:00 PM",
    averageRating: 1120,
  },
  {
    id: "batch-03",
    name: "Intermediate Batch B",
    level: "Intermediate",
    studentCount: 10,
    coach: "Coach Balaji K.",
    schedule: "Mon & Wed 5:00 PM - 6:30 PM",
    averageRating: 1470,
  },
  {
    id: "batch-04",
    name: "State Trials Elite Squad",
    level: "Advanced",
    studentCount: 12,
    coach: "Coach Ramesh S.",
    schedule: "Fri 6:00 PM & Sun 3:00 PM",
    averageRating: 1890,
  },
];

interface CoachRosterItem {
  id: string;
  name: string;
  title: string;
  studentsCount: number;
  batches: string;
  status: "Active" | "On Leave";
}

const COACHES: CoachRosterItem[] = [
  {
    id: "c-01",
    name: "Ramesh Sundararajan",
    title: "Head Coach (FIDE Trainer)",
    studentsCount: 26,
    batches: "Intermediate A, Elite Squad",
    status: "Active",
  },
  {
    id: "c-02",
    name: "Anandhi Venkatesan",
    title: "Senior Coach",
    studentsCount: 12,
    batches: "Junior Beginners",
    status: "Active",
  },
  {
    id: "c-03",
    name: "Balaji Krishnan",
    title: "Assistant Coach",
    studentsCount: 10,
    batches: "Intermediate B",
    status: "Active",
  },
];

export function AcademyDashboard({
  user,
}: AcademyDashboardProps): React.JSX.Element {
  const [tab, setTab] = useState<
    "overview" | "batches" | "coaches" | "settings"
  >("overview");

  return (
    <div className="role-dashboard academy-dashboard">
      <div className="academy-welcome-banner">
        <div>
          <h2>Academy Administration</h2>
          <p>
            {user.displayName} • Central Academy Registry • Territory:{" "}
            {user.territory}
          </p>
        </div>
        <nav className="dashboard-subnav" aria-label="Academy sections">
          <button
            type="button"
            className={`subnav-link ${tab === "overview" ? "active" : ""}`}
            onClick={() => setTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            className={`subnav-link ${tab === "batches" ? "active" : ""}`}
            onClick={() => setTab("batches")}
          >
            Cohorts & Batches
          </button>
          <button
            type="button"
            className={`subnav-link ${tab === "coaches" ? "active" : ""}`}
            onClick={() => setTab("coaches")}
          >
            Coaches
          </button>
          <button
            type="button"
            className={`subnav-link ${tab === "settings" ? "active" : ""}`}
            onClick={() => setTab("settings")}
          >
            Settings
          </button>
        </nav>
      </div>

      <div className="dashboard-content">
        <div className="stat-grid">
          <StatCard
            label="Total Enrolled Students"
            value="48"
            subtext="Across 4 Active Batches"
            trend={{ direction: "up", value: "+6 this month" }}
            icon="🎓"
          />
          <StatCard
            label="Certified Coaches"
            value={COACHES.length}
            subtext="FIDE / State Certified"
            icon="♟"
          />
          <StatCard
            label="Weekly Games"
            value="184"
            subtext="Rated games across cohorts"
            trend={{ direction: "up", value: "+18%" }}
            icon="⚔"
          />
          <StatCard
            label="Attendance Rate"
            value="94.6%"
            subtext="Academy average"
            icon="📊"
          />
        </div>

        {tab === "overview" && (
          <div className="academy-overview-tab">
            <div className="section-card">
              <h3>Active Academy Cohorts</h3>
              <div className="batches-grid">
                {BATCHES.map((b) => (
                  <div key={b.id} className="batch-card">
                    <div className="batch-card-top">
                      <h4>{b.name}</h4>
                      <span
                        className={`badge badge-${b.level === "Advanced" ? "accent" : b.level === "Intermediate" ? "success" : "neutral"}`}
                      >
                        {b.level}
                      </span>
                    </div>
                    <p className="batch-coach">Coach: {b.coach}</p>
                    <p className="batch-schedule">{b.schedule}</p>
                    <div className="batch-stats-row">
                      <span>
                        <strong>{b.studentCount}</strong> Students
                      </span>
                      <span>
                        Avg Rating: <strong>{b.averageRating}</strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "batches" && (
          <div className="academy-batches-tab">
            <div className="section-card">
              <div className="section-header-flex">
                <h3>Cohort & Batch Management</h3>
                <button type="button" className="btn-primary btn-sm">
                  + Create New Batch
                </button>
              </div>

              <div className="table-responsive">
                <table
                  className="data-table"
                  aria-label="Batches management table"
                >
                  <thead>
                    <tr>
                      <th>Batch Name</th>
                      <th>Level</th>
                      <th>Students</th>
                      <th>Lead Coach</th>
                      <th>Schedule</th>
                      <th>Avg Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BATCHES.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <strong>{b.name}</strong>
                        </td>
                        <td>{b.level}</td>
                        <td>{b.studentCount}</td>
                        <td>{b.coach}</td>
                        <td>{b.schedule}</td>
                        <td>{b.averageRating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "coaches" && (
          <div className="academy-coaches-tab">
            <div className="section-card">
              <h3>Academy Coaching Staff</h3>
              <div className="table-responsive">
                <table className="data-table" aria-label="Coaches table">
                  <thead>
                    <tr>
                      <th>Coach Name</th>
                      <th>Title / Credential</th>
                      <th>Assigned Students</th>
                      <th>Assigned Batches</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COACHES.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <strong>{c.name}</strong>
                        </td>
                        <td>{c.title}</td>
                        <td>{c.studentsCount}</td>
                        <td>{c.batches}</td>
                        <td>
                          <span className="badge badge-success">
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div className="academy-settings-tab">
            <div className="section-card">
              <h3>Academy Organization Profile</h3>
              <div className="account-form-grid">
                <div>
                  <span className="field-label">Organization Name</span>
                  <p className="field-val">Tamil Nadu Chess Academy</p>
                </div>
                <div>
                  <span className="field-label">Territory & State</span>
                  <p className="field-val">Tamil Nadu, India (IN-TN)</p>
                </div>
                <div>
                  <span className="field-label">
                    State Association Affiliation
                  </span>
                  <p className="field-val">
                    Tamil Nadu State Chess Association (TNSCA)
                  </p>
                </div>
                <div>
                  <span className="field-label">Enterprise Entitlement</span>
                  <p className="field-val">
                    Academy Institutional Tier (Unlimited Students)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
